import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { teboxDesignSystem, bookingBrandFontFamilyVar, useBookingTebox, bookingBrandInfoChromeStyles } from '../../styles/tebox-booking';
import { apiClient } from '../../services/api';
import { customerAuthService } from '../../services/customer-auth.service';
import { brandSettingsService, type BrandSettings } from '../../services/brand-settings';
import { BOOKING_PARTNER_INVITE_PENDING_CLAIM_KEY } from '../../utils/club-route-key';
import { resolveBookingBrandColors } from '../../utils/booking-brand-colors';
import LoginRegisterModal from './LoginRegisterModal';

type PartnerInviteResolveData = {
  reservationId: string;
  courseName: string;
  teeTimeLabel: string;
  hostDisplayName: string;
  openSlots: number;
  clubId: string;
};

type ResolveResponse = { success: boolean; data: PartnerInviteResolveData };
type ClaimResponse = {
  success: boolean;
  data: { reservationId: string; spotNumber: number; alreadyJoined: boolean };
};

/**
 * Full-page flow: open invite link → sign in (same club) → claim open player slot.
 * Route: `/join/:inviteToken`
 */
const ReservationPartnerJoinPage: React.FC = () => {
  const { inviteToken: inviteTokenParam } = useParams<{ inviteToken: string }>();
  const navigate = useNavigate();
  const tebox = useBookingTebox();
  const inviteToken = inviteTokenParam ? decodeURIComponent(inviteTokenParam) : '';

  const [resolveLoading, setResolveLoading] = useState(true);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [invite, setInvite] = useState<PartnerInviteResolveData | null>(null);

  const [brandColors, setBrandColors] = useState<BrandSettings | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claimDone, setClaimDone] = useState<ClaimResponse['data'] | null>(null);

  const oauthResumeRef = useRef(false);

  const clubRouteKey = useMemo(() => (invite ? `i:${invite.clubId}` : ''), [invite]);

  useEffect(() => {
    if (!inviteToken) {
      setResolveError('Invalid invite link.');
      setResolveLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setResolveLoading(true);
      setResolveError(null);
      try {
        const enc = encodeURIComponent(inviteToken);
        const res = await apiClient.get<ResolveResponse>(`/public/reservations/partner-invite/${enc}`);
        if (cancelled) return;
        if (!res.success || !res.data) {
          setResolveError('This invite could not be loaded.');
          return;
        }
        setInvite(res.data);
      } catch (e: unknown) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'This invite link is not valid or has expired.';
        setResolveError(msg);
      } finally {
        if (!cancelled) setResolveLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  useEffect(() => {
    if (!clubRouteKey) return;
    let cancelled = false;
    (async () => {
      const settings = await brandSettingsService.getByClubId(clubRouteKey);
      if (!cancelled) {
        setBrandColors(settings);
        await brandSettingsService.applyBrandSettings(settings);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clubRouteKey]);

  const clearPartnerInvitePendingClaim = useCallback(() => {
    try {
      sessionStorage.removeItem(BOOKING_PARTNER_INVITE_PENDING_CLAIM_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    oauthResumeRef.current = false;
  }, [inviteToken]);

  const runClaim = useCallback(
    async (tokenFromAuth?: string | null) => {
      if (!inviteToken) return;
      const trimmed = typeof tokenFromAuth === 'string' ? tokenFromAuth.trim() : '';
      const access = trimmed || customerAuthService.getAccessToken();
      if (!access) {
        setShowLogin(true);
        return;
      }
      setClaimLoading(true);
      setClaimError(null);
      try {
        apiClient.setAuthToken(access);
        const enc = encodeURIComponent(inviteToken);
        const res = await apiClient.post<ClaimResponse>(
          `/public/reservations/partner-invite/${enc}/claim`,
          {}
        );
        if (!res.success || !res.data) {
          clearPartnerInvitePendingClaim();
          setClaimError('Could not join this reservation.');
          return;
        }
        clearPartnerInvitePendingClaim();
        setClaimDone(res.data);
      } catch (e: unknown) {
        clearPartnerInvitePendingClaim();
        const msg = e instanceof Error ? e.message : 'Could not join this reservation.';
        setClaimError(msg);
      } finally {
        setClaimLoading(false);
      }
    },
    [inviteToken, clearPartnerInvitePendingClaim]
  );

  /** After Google OAuth we land on `/oauth/callback/google`; modal sends us back here with token in storage. */
  useEffect(() => {
    if (resolveLoading || resolveError || !invite || claimDone || claimLoading || showLogin) return;
    const pending = sessionStorage.getItem(BOOKING_PARTNER_INVITE_PENDING_CLAIM_KEY);
    if (!pending || pending !== inviteToken) return;
    const access = customerAuthService.getAccessToken();
    if (!access) return;
    if (oauthResumeRef.current) return;
    oauthResumeRef.current = true;
    void runClaim(access);
  }, [
    resolveLoading,
    resolveError,
    invite,
    inviteToken,
    claimDone,
    claimLoading,
    showLogin,
    runClaim,
  ]);

  const onLoginSuccess = useCallback((_customer: unknown, auth?: { token: string }) => {
    setShowLogin(false);
    const tokenHint = auth?.token?.trim() || customerAuthService.getAccessToken() || '';
    queueMicrotask(() => void runClaim(tokenHint || null));
  }, [runClaim]);

  /** Align with club Brand Settings (same tokens as OnlineBookingEngine / CSS vars). */
  const pageTheme = useMemo(() => {
    const b = brandColors;
    const text = b?.textColor ?? tebox.colors.textPrimary;
    const textMuted = b?.secondaryColor ?? tebox.colors.textSecondary;
    const pageBg = b?.backgroundColor ?? tebox.colors.bgPrimary;
    const border = b?.borderColor ?? tebox.colors.border;
    const btnBg = b?.buttonColor ?? tebox.colors.textPrimary;
    const btnText = b?.primaryTextColor ?? b?.buttonTextColor ?? '#FFFFFF';
    const radius =
      b?.borderRadius && b.borderRadius !== '0px' ? b.borderRadius : teboxDesignSystem.borderRadius.lg;
    const cardChrome = b
      ? bookingBrandInfoChromeStyles({
          primaryColor: b.primaryColor,
          buttonColor: b.buttonColor,
        })
      : {
          backgroundColor: tebox.colors.bgSecondary,
          border: `1px solid ${tebox.colors.border}`,
        };
    const accentInk = `color-mix(in srgb, ${(b?.primaryColor ?? b?.buttonColor ?? '#000000').trim()} 46%, black)`;
    return {
      text,
      textMuted,
      pageBg,
      border,
      btnBg,
      btnText,
      radius,
      cardChrome,
      logoUrl: b?.logoUrl,
      progressColor: b?.buttonColor ?? tebox.colors.textSecondary,
      accentInk,
    };
  }, [brandColors, tebox.colors]);

  if (resolveLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          boxSizing: 'border-box',
          backgroundColor: pageTheme.pageBg,
          fontFamily: bookingBrandFontFamilyVar,
        }}
      >
        <CircularProgress size={28} sx={{ color: pageTheme.progressColor }} />
      </Box>
    );
  }

  if (resolveError || !invite) {
    return (
      <Box
        sx={{
          maxWidth: 520,
          mx: 'auto',
          px: 2,
          py: 4,
          minHeight: '100vh',
          boxSizing: 'border-box',
          backgroundColor: pageTheme.pageBg,
          fontFamily: bookingBrandFontFamilyVar,
        }}
      >
        <Typography
          component="h1"
          sx={{
            ...teboxDesignSystem.typography.h4,
            fontFamily: 'inherit',
            fontWeight: 500,
            color: pageTheme.text,
            mb: 1.5,
          }}
        >
          Invite link
        </Typography>
        <Typography sx={{ ...teboxDesignSystem.typography.bodySmall, color: pageTheme.textMuted, mb: 2.5 }}>
          {resolveError || 'Something went wrong.'}
        </Typography>
        <Button
          sx={{
            fontFamily: 'inherit',
            borderColor: pageTheme.border,
            color: pageTheme.text,
            borderRadius: pageTheme.radius,
          }}
          variant="outlined"
          onClick={() => navigate('/', { replace: true })}
        >
          Back to booking
        </Button>
      </Box>
    );
  }

  if (claimDone) {
    return (
      <Box
        sx={{
          maxWidth: 520,
          mx: 'auto',
          px: 2,
          py: 4,
          minHeight: '100vh',
          boxSizing: 'border-box',
          backgroundColor: pageTheme.pageBg,
          fontFamily: bookingBrandFontFamilyVar,
        }}
      >
        {pageTheme.logoUrl ? (
          <Box
            component="img"
            src={pageTheme.logoUrl}
            alt=""
            sx={{ maxHeight: 48, width: 'auto', display: 'block', mb: 2, objectFit: 'contain' }}
          />
        ) : null}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: teboxDesignSystem.spacing.sm,
            mb: teboxDesignSystem.spacing.stackGap,
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: '50%',
              backgroundColor: pageTheme.btnBg,
              color: pageTheme.btnText,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-hidden
          >
            <Check size={24} strokeWidth={2.5} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                ...teboxDesignSystem.typography.h4,
                fontFamily: 'inherit',
                fontWeight: 500,
                color: pageTheme.text,
                mb: 1,
              }}
            >
              {claimDone.alreadyJoined ? "You're on this reservation" : "You're in!"}
            </Typography>
            <Typography sx={{ ...teboxDesignSystem.typography.bodySmall, color: pageTheme.textMuted }}>
              {claimDone.alreadyJoined
                ? 'Your account is already linked to this tee time.'
                : `You're linked as player ${claimDone.spotNumber}. This tee time will show with your other reservations.`}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            borderRadius: pageTheme.radius,
            ...pageTheme.cardChrome,
            p: teboxDesignSystem.spacing.md,
            mb: teboxDesignSystem.spacing.sectionGap,
          }}
        >
          <Typography
            component="h2"
            sx={{
              ...teboxDesignSystem.typography.formCardSectionTitle,
              fontFamily: 'inherit',
              color: pageTheme.text,
              mb: teboxDesignSystem.spacing.stackGapTight,
            }}
          >
            Confirmed round
          </Typography>
          <Typography sx={{ ...teboxDesignSystem.typography.body, fontWeight: 500, color: pageTheme.text }}>
            {invite.courseName}
          </Typography>
          <Typography
            sx={{
              ...teboxDesignSystem.typography.bodySmall,
              color: pageTheme.textMuted,
              mt: teboxDesignSystem.spacing.microGap,
            }}
          >
            {invite.teeTimeLabel}
          </Typography>
          <Typography
            sx={{
              ...teboxDesignSystem.typography.caption,
              color: pageTheme.textMuted,
              mt: teboxDesignSystem.spacing.sm,
            }}
          >
            Invited by{' '}
            <Box component="span" sx={{ fontWeight: 500, color: pageTheme.text }}>
              {invite.hostDisplayName}
            </Box>
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={() => navigate('/', { replace: true })}
          sx={{
            fontFamily: 'inherit',
            backgroundColor: pageTheme.btnBg,
            color: pageTheme.btnText,
            borderRadius: pageTheme.radius,
            boxShadow: 'none',
            '&:hover': { backgroundColor: pageTheme.btnBg, opacity: 0.92, boxShadow: 'none' },
          }}
        >
          Open booking
        </Button>
      </Box>
    );
  }

  const brandForModal = brandColors ? resolveBookingBrandColors(brandColors) : undefined;

  const signedIn = Boolean(customerAuthService.getAccessToken());

  return (
    <Box
      sx={{
        maxWidth: 520,
        mx: 'auto',
        px: 2,
        py: 4,
        minHeight: '100vh',
        boxSizing: 'border-box',
        backgroundColor: pageTheme.pageBg,
        fontFamily: bookingBrandFontFamilyVar,
      }}
    >
      {pageTheme.logoUrl ? (
        <Box
          component="img"
          src={pageTheme.logoUrl}
          alt=""
          sx={{ maxHeight: 48, width: 'auto', display: 'block', mb: 2, objectFit: 'contain' }}
        />
      ) : null}
      <Typography
        component="h1"
        sx={{
          ...teboxDesignSystem.typography.h4,
          fontFamily: 'inherit',
          fontWeight: 500,
          color: pageTheme.text,
          mb: 1.5,
        }}
      >
        You're invited to this tee time
      </Typography>

      <Typography
        sx={{
          ...teboxDesignSystem.typography.body,
          color: pageTheme.text,
          mb: teboxDesignSystem.spacing.stackGap,
        }}
      >
        <strong style={{ color: pageTheme.accentInk }}>{invite.hostDisplayName}</strong> shared a link so you can join
        their reservation and fill an open player spot. Use the steps below, then confirm with the button.
      </Typography>

      <Box
        sx={{
          borderRadius: pageTheme.radius,
          ...pageTheme.cardChrome,
          padding: teboxDesignSystem.spacing.md,
          mb: teboxDesignSystem.spacing.sectionGap,
        }}
      >
        <Typography
          component="h2"
          sx={{
            ...teboxDesignSystem.typography.formCardSectionTitle,
            fontFamily: 'inherit',
            color: pageTheme.text,
            mb: teboxDesignSystem.spacing.stackGapTight,
          }}
        >
          Round details
        </Typography>
        <Typography sx={{ ...teboxDesignSystem.typography.body, fontWeight: 500, color: pageTheme.text }}>
          {invite.courseName}
        </Typography>
        <Typography
          sx={{
            ...teboxDesignSystem.typography.bodySmall,
            color: pageTheme.textMuted,
            mt: teboxDesignSystem.spacing.microGap,
          }}
        >
          {invite.teeTimeLabel}
        </Typography>
        <Typography
          sx={{
            ...teboxDesignSystem.typography.caption,
            color: pageTheme.textMuted,
            mt: teboxDesignSystem.spacing.sm,
          }}
        >
          {invite.openSlots > 0
            ? `${invite.openSlots} open spot${invite.openSlots === 1 ? '' : 's'} remaining`
            : 'No open spots'}
        </Typography>
      </Box>

      {invite.openSlots <= 0 ? (
        <Typography sx={{ ...teboxDesignSystem.typography.bodySmall, color: pageTheme.textMuted }}>
          This group is full. Ask the host to adjust the booking if you still need a spot.
        </Typography>
      ) : (
        <>
          <Typography
            component="h2"
            sx={{
              ...teboxDesignSystem.typography.formCardSectionTitle,
              fontFamily: 'inherit',
              color: pageTheme.text,
              mb: teboxDesignSystem.spacing.stackGapTight,
            }}
          >
            What to do next
          </Typography>
          <Box
            component="ol"
            sx={{
              ...teboxDesignSystem.typography.bodySmall,
              color: pageTheme.text,
              m: 0,
              pl: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: teboxDesignSystem.spacing.sm,
              mb: teboxDesignSystem.spacing.sectionGap,
              '& li::marker': {
                fontWeight: 500,
                color: pageTheme.text,
              },
            }}
          >
            <Box component="li" sx={{ pl: 0.5 }}>
              {signedIn ? (
                <>
                  You're signed in. If this isn't the right golfer account, sign out from the main booking site and open
                  this link again.
                </>
              ) : (
                <>
                  Sign in with your golfer account for this club. The button below opens a secure login; use the same
                  email you normally use to book here.
                </>
              )}
            </Box>
            <Box component="li" sx={{ pl: 0.5 }}>
              Tap <strong>Join this round</strong>. We'll attach you to this reservation on the next open spot.
            </Box>
            <Box component="li" sx={{ pl: 0.5 }}>
              After you join, you'll see this tee time with your other reservations. The host can still change player or
              cart details on their side.
            </Box>
          </Box>

          {claimError ? (
            <Typography
              sx={{
                ...teboxDesignSystem.typography.caption,
                color: 'error.main',
                mb: teboxDesignSystem.spacing.stackGapTight,
              }}
              role="alert"
            >
              {claimError}
            </Typography>
          ) : null}
          <Button
            variant="contained"
            disabled={claimLoading}
            onClick={() => void runClaim()}
            fullWidth
            sx={{
              py: 1.25,
              ...teboxDesignSystem.typography.body,
              fontFamily: 'inherit',
              fontWeight: 500,
              backgroundColor: pageTheme.btnBg,
              color: pageTheme.btnText,
              borderRadius: pageTheme.radius,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: pageTheme.btnBg,
                opacity: claimLoading ? 1 : 0.92,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: pageTheme.btnBg,
                color: pageTheme.btnText,
                opacity: 0.65,
              },
            }}
          >
            {claimLoading ? (
              <CircularProgress size={20} sx={{ color: pageTheme.btnText }} />
            ) : signedIn ? (
              'Join this round'
            ) : (
              'Sign in and join this round'
            )}
          </Button>
        </>
      )}

      <LoginRegisterModal
        open={showLogin}
        onClose={() => setShowLogin(false)}
        onSuccess={onLoginSuccess}
        clubId={invite.clubId}
        brandColors={brandForModal}
        teeTimeInfo={{
          dateTime: invite.teeTimeLabel,
          courseName: invite.courseName,
          price: 0,
        }}
      />
    </Box>
  );
};

export default ReservationPartnerJoinPage;
